import { useEffect, useState } from 'react'
import type { PDFPageProxy } from 'pdfjs-dist'
import type { TextItem } from 'pdfjs-dist/types/src/display/api'
import { normaliser } from '../lib/devisTexte'
import type { Zone } from '../lib/types'
import type { SourcePages } from '../screens/VisualiseurPdf'

/**
 * Surlignage des termes cherchés sur la page affichée (devis).
 *
 * Le texte de la page est relu avec sa position, mis bout à bout comme à la
 * recherche (sans accents ni majuscules, tirets = espaces), puis chaque
 * occurrence devient un rectangle jaune en coordonnées normalisées (0 à 1) :
 * il suit le zoom comme les annotations.
 */
export async function zonesDesTermes(p: PDFPageProxy, termes: string[]): Promise<Zone[]> {
  const vue = p.getViewport({ scale: 1 })
  const contenu = await p.getTextContent()
  const items = contenu.items.filter((i): i is TextItem => 'str' in i)
  const boites = items.map((it) => {
    const [x, y] = vue.convertToViewportPoint(it.transform[4], it.transform[5])
    const h = Math.hypot(it.transform[2], it.transform[3]) || 8
    return { x, y, l: it.width, h }
  })

  // Texte de la page, et pour chaque caractère : l'élément et la position d'où il vient.
  let texte = ''
  const carte: { i: number; c: number }[] = []
  let prec = -1
  items.forEach((it, i) => {
    if (!it.str) return
    if (prec >= 0) {
      const a = boites[prec]
      const b = boites[i]
      const memeLigne = Math.abs(a.y - b.y) <= Math.max(2, Math.min(a.h, b.h) * 0.45)
      const colle =
        (memeLigne && b.x - (a.x + a.l) <= b.h * 0.2) ||
        (!memeLigne && /\p{L}-$/u.test(items[prec].str)) // mot coupé en fin de ligne
      if (!colle && !texte.endsWith(' ') && !it.str.startsWith(' ')) {
        texte += ' '
        carte.push({ i: -1, c: 0 })
      }
    }
    for (let c = 0; c < it.str.length; c++) {
      texte += it.str[c]
      carte.push({ i, c })
    }
    prec = i
  })

  const norm = normaliser(texte)
  const zones: Zone[] = []
  for (const t of termes) {
    if (!t) continue
    let k = norm.indexOf(t)
    while (k >= 0) {
      // Un rectangle par élément de texte touché (une expression peut
      // s'étendre sur plusieurs morceaux, ou sur deux lignes).
      let j = k
      while (j < k + t.length) {
        const { i } = carte[j]
        if (i < 0) {
          j++
          continue
        }
        let debut = carte[j].c
        let fin = debut
        while (j + 1 < k + t.length && carte[j + 1].i === i) {
          j++
          fin = carte[j].c
        }
        j++
        const b = boites[i]
        const n = Math.max(1, items[i].str.length)
        const x0 = b.x + (b.l * debut) / n
        const x1 = b.x + (b.l * (fin + 1)) / n
        zones.push({
          x: x0 / vue.width,
          y: (b.y - b.h * 0.9) / vue.height,
          l: Math.max(x1 - x0, 2) / vue.width,
          h: (b.h * 1.15) / vue.height
        })
      }
      k = norm.indexOf(t, k + t.length)
    }
  }
  return zones
}

export function CoucheSurlignage({
  source,
  page,
  termes
}: {
  source: SourcePages
  page: number
  termes: string[]
}) {
  const [zones, setZones] = useState<Zone[]>([])
  const cle = termes.join('\u0000')

  useEffect(() => {
    let actif = true
    setZones([])
    source
      .page(page)
      .then((p) => zonesDesTermes(p, termes))
      .then((z) => actif && setZones(z))
      .catch(() => {})
    return () => {
      actif = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, page, cle])

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1 }} aria-hidden>
      {zones.map((z, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: `${z.x * 100}%`,
            top: `${z.y * 100}%`,
            width: `${z.l * 100}%`,
            height: `${z.h * 100}%`,
            background: 'rgba(255, 202, 0, 0.45)',
            mixBlendMode: 'multiply',
            outline: '1px solid rgba(217, 171, 0, 0.9)'
          }}
        />
      ))}
    </div>
  )
}
